import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { runOneDriveHealthCheckWithNotifications } from '../../utils/testOneDriveHealth';

/**
 * Panneau de diagnostic pour OneDrive et l'authentification Azure
 */
const DiagnosticsPanel = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [lastRun, setLastRun] = useState(null);

  const runDiagnostics = async () => {
    setLoading(true);
    try {
      const diagnosticResults = await runOneDriveHealthCheckWithNotifications();
      setResults(diagnosticResults);
      setLastRun(new Date());
    } catch (error) {
      console.error('Erreur lors du diagnostic:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (success, warning = false) => {
    if (warning) return <WarningIcon sx={{ color: 'warning.main' }} />;
    return success ? 
      <CheckIcon sx={{ color: 'success.main' }} /> : 
      <ErrorIcon sx={{ color: 'error.main' }} />;
  };

  const getStatusColor = (success, warning = false) => {
    if (warning) return 'warning';
    return success ? 'success' : 'error';
  };

  const formatData = (data) => {
    if (typeof data === 'object' && data !== null) {
      return JSON.stringify(data, null, 2);
    }
    return String(data);
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" component="h2">
          Diagnostic OneDrive
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} /> : <PlayIcon />}
            onClick={runDiagnostics}
            disabled={loading}
          >
            {loading ? 'Test en cours...' : 'Exécuter les tests'}
          </Button>
          {results && (
            <Tooltip title="Actualiser les tests">
              <IconButton onClick={runDiagnostics} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {results && (
        <>
          {/* Résumé */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color={results.status === 'Excellent' ? 'success.main' : 
                                                   results.status === 'Bon' ? 'info.main' : 
                                                   results.status === 'Moyen' ? 'warning.main' : 'error.main'}>
                    {results.healthScore}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Score de santé
                  </Typography>
                  <Chip 
                    label={results.status} 
                    color={results.status === 'Excellent' ? 'success' : 
                           results.status === 'Bon' ? 'info' : 
                           results.status === 'Moyen' ? 'warning' : 'error'}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">
                    {results.summary.passed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tests réussis
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main">
                    {results.summary.warnings}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avertissements
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="error.main">
                    {results.summary.failed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tests échoués
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Barre de progression */}
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Progression: {results.summary.passed}/{results.summary.total} tests
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={results.healthScore} 
              color={results.healthScore >= 80 ? 'success' : 
                     results.healthScore >= 60 ? 'info' : 
                     results.healthScore >= 40 ? 'warning' : 'error'}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>

          {/* Informations système */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Plateforme:</strong> {results.platform} | 
              <strong> Exécuté le:</strong> {new Date(results.timestamp).toLocaleString('fr-FR')} |
              <strong> Dernière actualisation:</strong> {lastRun?.toLocaleString('fr-FR')}
            </Typography>
          </Alert>

          {/* Détails des tests */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Détails des tests
          </Typography>
          
          {results.tests.map((test, index) => (
            <Accordion key={index}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  {getStatusIcon(test.success, test.warning)}
                  <Typography>{test.name}</Typography>
                  <Chip 
                    label={test.warning ? 'Warning' : test.success ? 'Succès' : 'Échec'} 
                    size="small"
                    color={getStatusColor(test.success, test.warning)}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Exécuté le: {new Date(test.timestamp).toLocaleString('fr-FR')}
                </Typography>
                
                {/* Afficher les données du test */}
                {Object.entries(test)
                  .filter(([key]) => !['name', 'success', 'warning', 'timestamp'].includes(key))
                  .map(([key, value]) => (
                    <Box key={key} mb={1}>
                      <Typography variant="body2" fontWeight="bold">
                        {key.charAt(0).toUpperCase() + key.slice(1)}:
                      </Typography>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 1, 
                          bgcolor: 'grey.50', 
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          overflow: 'auto',
                          maxHeight: '200px'
                        }}
                      >
                        <pre style={{ margin: 0 }}>
                          {formatData(value)}
                        </pre>
                      </Paper>
                    </Box>
                  ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}

      {!results && !loading && (
        <Alert severity="info">
          <Typography>
            Cliquez sur "Exécuter les tests" pour diagnostiquer la connexion OneDrive et l'authentification Azure.
          </Typography>
        </Alert>
      )}
    </Paper>
  );
};

export default DiagnosticsPanel;